import { useState, useEffect, useRef } from "react";
import { ArrowRight, Package } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSiteContent } from "@/lib/firestoreService";
import { asset } from "@/lib/siteAssets";
import { trackNavClick } from "@/lib/analytics";

const FALLBACK_BANNERS = [
  { src: asset("products/banner.webp"),  alt: "Annapurna Home Pickles" },
  { src: asset("products/banner1.webp"), alt: "Annapurna Home Pickles" },
];

export const Hero = () => {
  const { t, lang } = useLang();
  const site = useSiteContent();

  const rawBanners = site.banners?.length > 0 ? site.banners : FALLBACK_BANNERS;
  const headline_1 = lang === "en" ? site.hero_headline_1 : t.hero.headline_1;
  const headline_2 = lang === "en" ? site.hero_headline_2 : t.hero.headline_2;
  const headline_3 = lang === "en" ? site.hero_headline_3 : t.hero.headline_3;
  const subhead    = lang === "en" ? site.hero_subhead    : t.hero.subhead;

  // currentOffset = which set of 4 is sliding IN
  // prevOffset    = which set of 4 stays underneath (never blanks out)
  const [currentOffset, setCurrentOffset] = useState(0);
  const [prevOffset, setPrevOffset]       = useState(null);
  const rawBannersRef = useRef(rawBanners);
  rawBannersRef.current = rawBanners;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentOffset(o => {
        const next = (o + 1) % rawBannersRef.current.length;
        setPrevOffset(o);
        return next;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-6 -left-6 w-40 h-40 mango-leaf rotate-12 opacity-80" />
      <div className="pointer-events-none absolute -top-4 -right-10 w-48 h-48 mango-leaf -rotate-45 opacity-60" />
      <div className="pointer-events-none absolute -bottom-10 -left-10 w-44 h-44 mango-leaf -rotate-12 opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 sm:pt-5 pb-8 sm:pb-14">
        <div className="grid lg:grid-cols-12 gap-6 sm:gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 relative">
            <h1
              data-testid="hero-headline"
              className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#5A2D1D] leading-[1.1]"
            >
              {headline_1}
              <br />
              <span className="italic font-semibold text-[#7A3B25]">{headline_2}</span>
              <br />
              <span className="wavy-underline">{headline_3}</span>
            </h1>

            <p className="mt-5 text-sm sm:text-base text-[#2E1A12]/85 max-w-xl leading-relaxed">
              {subhead}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/products"
                data-testid="hero-order-now-button"
                onClick={() => trackNavClick("Order Now Button", "hero_section")}
                className="group inline-flex items-center gap-2 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] px-7 py-4 rounded-full font-semibold tracking-wide transition-colors shadow-md"
              >
                {t.cta.order_now}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/products"
                data-testid="hero-view-products-button"
                onClick={() => trackNavClick("View Products", "hero_section")}
                className="inline-flex items-center gap-2 border-2 border-[#5A2D1D] text-[#5A2D1D] hover:bg-[#5A2D1D] hover:text-[#F5E6CC] px-7 py-[14px] rounded-full font-semibold tracking-wide transition-colors"
              >
                {t.cta.view_products}
              </Link>
            </div>

            <div className="mt-8 grid grid-cols-3 max-w-md gap-4 text-sm">
              {[
                { n: "100%", l: t.hero.stat_homemade },
                { n: "0",    l: t.hero.stat_preservatives },
                { n: "5★",   l: t.hero.stat_reviews, to: "/about#testimonials" },
              ].map((s, idx) => {
                const content = (
                  <>
                    <div className="font-display text-2xl font-bold text-[#5A2D1D]">{s.n}</div>
                    <div className="uppercase text-[10px] tracking-[0.2em] text-[#556B2F] font-bold mt-1">{s.l}</div>
                  </>
                );
                return s.to ? (
                  <Link key={idx} to={s.to} className="hover:opacity-75 transition-opacity">{content}</Link>
                ) : (
                  <div key={idx}>{content}</div>
                );
              })}
            </div>

            {/* Bulk Orders CTA */}
            <div className="mt-6 max-w-md flex items-center justify-between gap-4 bg-[#EFD9B4]/70 border border-[#5A2D1D]/20 rounded-sm px-4 py-3">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[#556B2F] flex-shrink-0" />
                <div>
                  <div className="text-sm font-bold text-[#5A2D1D]">Bulk Orders Available</div>
                  <div className="text-xs text-[#2E1A12]/65">Weddings · Events · Corporate Gifting</div>
                </div>
              </div>
              <Link
                to="/contact"
                onClick={() => trackNavClick("Bulk Orders Available Contact Us", "hero_section")}
                className="flex-shrink-0 inline-flex items-center gap-1.5 bg-[#5A2D1D] hover:bg-[#7A3B25] text-[#F5E6CC] text-xs font-bold px-4 py-2 rounded-full transition-colors whitespace-nowrap"
              >
                Contact Us
              </Link>
            </div>
          </div>

          {/* ── 2×2 rotating banner grid ── */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/3] sm:aspect-[4/5] max-w-sm sm:max-w-md mx-auto">
              {/* decorative offset borders */}
              <div className="absolute inset-0 border-2 border-[#5A2D1D] translate-x-3 translate-y-3 rounded-sm" />
              <div className="absolute inset-0 border border-[#556B2F]/60 translate-x-6 translate-y-6 rounded-sm" />

              <div className="relative w-full h-full rounded-sm overflow-hidden shadow-2xl">
                  <div className="grid grid-cols-2 grid-rows-2 w-full h-full gap-2 bg-[#F5E6CC]">
                    {[0, 1, 2, 3].map(i => {
                      const current = rawBanners[(currentOffset + i) % rawBanners.length];
                      const prev    = prevOffset !== null
                        ? rawBanners[(prevOffset + i) % rawBanners.length]
                        : current;
                      return (
                        <Link
                          key={i}
                          to="/products"
                          onClick={() => trackNavClick("Hero Banner Image", "hero_section")}
                          className="relative overflow-hidden block"
                          data-testid={i === 0 ? "hero-image" : undefined}
                        >
                          {/* old image stays put — never blank */}
                          <img
                            src={prev.src}
                            alt={prev.alt}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading={i === 0 ? "eager" : "lazy"}
                            decoding="async"
                          />
                          {/* new image slides over from the right */}
                          <img
                            key={currentOffset}
                            src={current.src}
                            alt={current.alt}
                            className={`absolute inset-0 w-full h-full object-cover banner-cell-${i}`}
                            fetchPriority={i === 0 ? "high" : "low"}
                            decoding="async"
                          />
                        </Link>
                      );
                    })}
                  </div>

                {/* overlay text */}
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#2E1A12]/85 via-[#2E1A12]/30 to-transparent">
                  <div className="font-script text-xl text-[#F5E6CC]">{t.hero.made_with_love}</div>
                  <div className="font-display text-base text-[#F5E6CC] font-semibold">{t.hero.small_batches}</div>
                </div>
              </div>

              {/* badge */}
              <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-[#556B2F] text-[#F5E6CC] grid place-items-center text-center font-display rotate-[12deg] shadow-xl border-4 border-[#F5E6CC]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest">{t.hero.badge_pure}</div>
                  <div className="text-lg font-bold leading-none">{t.hero.badge_andhra}</div>
                  <div className="text-[10px] uppercase tracking-widest">{t.hero.badge_taste}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
